import { ref, Ref } from 'vue';
import _ from 'lodash-es';
import { IPromise, IResponse } from '@/core/http/type';

interface CustomObj { [propName: string]: any; }

/* 选择项 */
export interface Opt extends CustomObj {
  label: string; // 中文映射
  value: any; // 选中值
}

/**
 * T 请求体类型
 * R 响应体类型
 */
interface Option<T, R> {
  params: T;
  keywordKey?: keyof T | string; // 搜索 [query 关键字] 同步到 params[keywordKey]
  defaultQuery?: boolean; // 初始化默认请求
  defaultQueryKeyWord?: string; // 初始化默认请求 关键字
  resPath?: keyof IResponse | string; // 响应体获取【option数组】路径
  API: (params: T) => IPromise<R>;
  beforeRequest?: (params: T, query: string) => T; // 请求前 params 处理
  beforeResponse?: (response: IResponse<R & any>) => Opt[]; // 响应前 response 处理 (返回option数组)
}

export interface FuzzyRes {
  fuzzyLoading: Ref<boolean>;
  fuzzyResponse: Ref<Opt[]>;
  fuzzyRemoteMethod: _.DebouncedFunc<(query: string) => Promise<void>>;
}

/**
 * 远程模糊搜索
 * @param { Option } option
 * @returns
 */
export function useFuzzy<T extends CustomObj, R = CustomObj>(option: Option<T, R>): FuzzyRes {
  const {
    API,
    params,
    keywordKey,
    defaultQuery = false,
    defaultQueryKeyWord = '',
    beforeRequest,
    beforeResponse,
    resPath = 'data',
  } = option;

  const fuzzyLoading = ref<boolean>(false);
  const fuzzyResponse = ref<Opt[]>([]);
  const fuzzyRemoteMethod = _.debounce(
    async (query: string) => {
      if (query === '') return;
      fuzzyLoading.value = true;
      try {
        keywordKey && (params[keywordKey as keyof T] = query as T[keyof T]);
        const _params = beforeRequest ? beforeRequest(params, query) : params;
        const res = await API(_params);
        const _res = beforeResponse ? beforeResponse(res) : _.get(res, resPath);
        fuzzyResponse.value = _res ?? [];
      } catch (e) {
        fuzzyResponse.value = [];
        console.log('useFuzzy error', e);
      } finally {
        fuzzyLoading.value = false;
      }
    },
    200,
  );

  defaultQuery && fuzzyRemoteMethod(defaultQueryKeyWord);

  return {
    fuzzyLoading,
    fuzzyResponse,
    fuzzyRemoteMethod,
  };
}
